import { useEffect, useState, useCallback } from "react";
import { Loader2, Flame, MessageCircleHeart, Check, EyeOff, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [
  { value: "pending", label: "Awaiting review" },
  { value: "approved", label: "Live on wall" },
  { value: "rejected", label: "Hidden" },
  { value: "", label: "All" },
];
const statusStyle = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-slate-200 text-slate-600",
};

export function DedicationsPanel({ token, onUnauthorized }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const url = filter ? `${API}/admin/dedications?status=${filter}` : `${API}/admin/dedications`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return onUnauthorized();
      setItems(await res.json());
    } catch {
      toast.error("Could not load dedications");
    }
  }, [token, filter, onUnauthorized]);

  useEffect(() => { setItems(null); load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      const res = await fetch(`${API}/admin/dedications/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(status === "approved" ? "Now live on the wall" : "Hidden from the wall");
      load();
    } catch {
      toast.error("Could not update dedication");
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this dedication?")) return;
    setBusy(id);
    try {
      const res = await fetch(`${API}/admin/dedications/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      toast.success("Dedication deleted");
      load();
    } catch {
      toast.error("Could not delete dedication");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-dedications-panel">
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            data-testid={`dedication-filter-${f.value || "all"}`}
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-5 py-2 text-sm font-bold border-2 transition-colors ${
              filter === f.value ? "bg-[#5B21B6] border-[#5B21B6] text-white" : "border-purple-200 text-[#5B21B6] hover:bg-[#F3E8FF]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#5B21B6]" /></div>
      ) : items.length === 0 ? (
        <div data-testid="dedications-empty-state" className="text-center py-20 bg-white rounded-3xl border border-purple-100">
          <Sparkles size={40} className="mx-auto text-amber-400 mb-4" />
          <p className="font-display font-bold text-xl text-[#2E1065]">
            {filter === "pending" ? "Nothing waiting for review" : "No dedications here"}
          </p>
          <p className="text-sm text-slate-500 mt-1">New candles and messages from the Butterfly Wall will show up here for approval.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="admin-dedications-grid">
          {items.map((d) => (
            <div key={d.id} data-testid={`admin-dedication-${d.id}`} className="bg-white rounded-3xl border border-purple-100 shadow-sm p-6 flex flex-col">
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${d.kind === "candle" ? "text-[#B45309]" : "text-[#5B21B6]"}`}>
                  {d.kind === "candle" ? <Flame size={13} /> : <MessageCircleHeart size={13} />}
                  {d.kind === "candle" ? "Candle" : "Message"}
                </span>
                <span data-testid={`dedication-status-${d.id}`} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${statusStyle[d.status] || statusStyle.pending}`}>
                  {d.status}
                </span>
              </div>
              <p className="font-display italic text-[#2E1065] leading-relaxed flex-1">{d.message ? `"${d.message}"` : <span className="text-slate-400 not-italic text-sm">(no message)</span>}</p>
              <p className="mt-3 text-xs text-slate-500">
                <span className="font-bold text-[#2E1065]">{d.name}</span> · {new Date(d.created_at).toLocaleString()}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {d.status !== "approved" && (
                  <button type="button" disabled={busy === d.id} data-testid={`approve-dedication-${d.id}`} onClick={() => setStatus(d.id, "approved")}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors">
                    <Check size={14} /> Approve
                  </button>
                )}
                {d.status !== "rejected" && (
                  <button type="button" disabled={busy === d.id} data-testid={`reject-dedication-${d.id}`} onClick={() => setStatus(d.id, "rejected")}
                    className="inline-flex items-center gap-1.5 border-2 border-slate-300 hover:bg-slate-100 disabled:opacity-60 text-slate-600 text-xs font-bold px-4 py-2 rounded-full transition-colors">
                    <EyeOff size={14} /> Hide
                  </button>
                )}
                <button type="button" disabled={busy === d.id} data-testid={`delete-dedication-${d.id}`} onClick={() => remove(d.id)}
                  className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50 disabled:opacity-60 text-xs font-bold px-4 py-2 rounded-full transition-colors ml-auto">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
